/**
 * TRIPVENTURA MODERN MODAL GALLERY - ENHANCED VERSION
 * Professional image gallery modal with responsive design and accessibility features
 * Supports dynamic liquid sections, keyboard navigation, touch gestures, and screen readers
 * @version 2.0.0
 */

/**
 * Main gallery modal class for displaying product images in a full-screen overlay
 * @class TripventuraModalGallery
 */
class TripventuraModalGallery {
  /**
   * Creates a new modal gallery instance
   * @param {string|null} sectionId - Optional section ID for liquid-based galleries
   */
  constructor(sectionId = null) {
    this.sectionId = sectionId;
    this.config = sectionId
      ? window[`tripventuraGalleryConfig_${sectionId}`] || {}
      : window.modalGalleryConfig || {};

    this.currentIndex = 0;
    this.activeTab = 'tripventura';

    // Handle images from config
    if (this.config.product?.images) {
      if (Array.isArray(this.config.product.images)) {
        this.images = {
          tripventura: this.config.product.images,
          snapshots: []
        };
      } else {
        this.images = this.config.product.images;
      }
    } else {
      this.images = { tripventura: [], snapshots: [] };
    }

    this.modal = null;
    this.isOpen = false;
    this.focusableElements = [];
    this.previouslyFocusedElement = null;
    this.preloadedImages = new Map();
    this.originalBodyOverflow = '';
    this.originalBodyPaddingRight = '';
    this.originalHtmlOverflow = '';

    // Bind methods
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.navigateImage = this.navigateImage.bind(this);
    this.handleKeyboard = this.handleKeyboard.bind(this);
    this.handleSwipe = this.handleSwipe.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.preventTouchMove = this.preventTouchMove.bind(this);

    this.init();
  }

  /**
   * Initializes the modal gallery by creating DOM elements, attaching event listeners, and preloading images
   * @returns {void}
   */
  init() {
    this.createModal();
    this.attachEventListeners();
    this.preloadImages();
  }

  /**
   * Creates or retrieves the modal DOM structure
   * For liquid sections, finds existing modal in DOM; otherwise creates it dynamically
   * @returns {void}
   */
  createModal() {
    // If using liquid section, the modal HTML is already in DOM
    if (this.sectionId) {
      this.modal = document.getElementById(`tripventura-gallery-modal-${this.sectionId}`);
      if (this.modal) {
        this.updateFocusableElements();
        return;
      }
    }

    // Fallback: create modal dynamically (for non-liquid usage)
    const existingModal = document.querySelector('.tripventura-modal');
    if (existingModal) {
      existingModal.remove();
    }

    // Get current images for active tab
    const currentImages = this.images[this.activeTab];

    // Create modal HTML with tabs
    const modalHTML = `
      <div class="tripventura-modal" id="tripventura-gallery-modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <!-- Focus trap start -->
        <div class="modal-focus-trap" tabindex="0"></div>

        <div class="modal-content">
          <!-- Header with Tabs -->
          <div class="modal-header">
            <h2 class="modal-title">${this.config.settings?.modalTitle || '[Kids Go Free] Louvre Abu Dhabi Skip-the-Line Tickets'}</h2>
            <div class="modal-tabs">
              <button class="modal-tab active" data-tab="tripventura" type="button">${this.config.settings?.tabName || 'Tripventura gallery'}</button>
            </div>
            <button class="modal-close-btn" aria-label="${this.config.accessibility?.closeLabel || 'Close gallery'}" type="button">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor"/>
              </svg>
            </button>
          </div>

          <!-- Main Content Area -->
          <div class="modal-main-content">
            <!-- Main Image Area -->
            <div class="modal-main-image-area">
              <div class="modal-main-image-container">
                <!-- Image Counter -->
                ${this.config.settings?.showCounter !== false ? `
                <div class="modal-counter">
                  <span class="current-image">1</span> / <span class="total-images">${currentImages.length}</span>
                </div>
                ` : ''}

                <div class="modal-loading" id="modal-loading"></div>
                <img class="modal-main-image" id="modal-main-image" alt="" />

                <!-- Navigation Buttons -->
                <button class="modal-nav-btn prev" aria-label="${this.config.accessibility?.prevLabel || 'Previous image'}" type="button">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button class="modal-nav-btn next" aria-label="${this.config.accessibility?.nextLabel || 'Next image'}" type="button">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Thumbnail Sidebar -->
            <div class="modal-thumbnails-sidebar">
              <div class="modal-thumbnails-container">
                <div class="modal-thumbnails-grid" id="modal-thumbnails-grid">
                  ${this.generateThumbnailsHTML()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Focus trap end -->
        <div class="modal-focus-trap" tabindex="0"></div>

        <!-- Screen reader description -->
        <div id="modal-description" class="sr-only">
          ${this.config.accessibility?.screenReaderInstructions || 'Use arrow keys to navigate between images, or click thumbnails to jump to specific images. Press Escape to close.'}
        </div>
      </div>
    `;

    // Insert modal into DOM
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('tripventura-gallery-modal');

    // Get focusable elements
    this.updateFocusableElements();
  }

  generateThumbnailsHTML() {
    const currentImages = this.images[this.activeTab];

    return currentImages.map((image, index) => `
      <div class="modal-thumb-item ${index === 0 ? 'active' : ''}"
           data-index="${index}"
           role="button"
           tabindex="0"
           aria-label="${this.config.accessibility?.thumbnailLabel || 'Gallery thumbnail'} ${index + 1}">
        <img src="${image.thumbnailSrc || image.src}" alt="${image.alt}" loading="lazy" />
        ${image.type === 'video' || image.type === 'external_video' ? `
          <div class="modal-thumb-badge">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
            </svg>
          </div>
        ` : ''}
      </div>
    `).join('');
  }

  attachEventListeners() {
    // Main gallery click handlers
    this.attachMainGalleryListeners();

    // Modal event listeners
    if (this.modal) {
      // Close button
      const closeBtn = this.modal.querySelector('.modal-close-btn');
      closeBtn?.addEventListener('click', this.closeModal);

      // Tab navigation
      this.modal.querySelectorAll('.modal-tab').forEach(tab => {
        tab.addEventListener('click', (e) => this.switchTab(e.target.dataset.tab));
      });

      // Navigation buttons
      const prevBtn = this.modal.querySelector('.modal-nav-btn.prev');
      const nextBtn = this.modal.querySelector('.modal-nav-btn.next');
      prevBtn?.addEventListener('click', () => this.navigateImage(-1));
      nextBtn?.addEventListener('click', () => this.navigateImage(1));

      // Thumbnail clicks
      const thumbnailsGrid = this.modal.querySelector('#modal-thumbnails-grid');
      thumbnailsGrid?.addEventListener('click', this.handleThumbnailClick.bind(this));
      thumbnailsGrid?.addEventListener('keydown', this.handleThumbnailKeydown.bind(this));

      // Outside click to close
      this.modal.addEventListener('click', this.handleOutsideClick);

      // Touch/swipe support
      this.attachSwipeListeners();

      // Focus trap
      this.attachFocusTrapListeners();
    }

    // Global event listeners
    document.addEventListener('keydown', this.handleKeyboard);
    window.addEventListener('resize', this.handleResize);
  }

  attachMainGalleryListeners() {
    // If using liquid section, attach to section-specific triggers
    if (this.sectionId) {
      const triggers = document.querySelectorAll(`[data-section-id="${this.sectionId}"] .tripventura-gallery-trigger-${this.sectionId}`);
      triggers.forEach((trigger) => {
        // Use capture phase to intercept early
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Disable any parent click handlers temporarily
          const parent = trigger.closest('.product__media-item, .product__media-list');
          if (parent) {
            parent.style.pointerEvents = 'none';
            setTimeout(() => parent.style.pointerEvents = '', 500);
          }

          const imageIndex = parseInt(trigger.getAttribute('data-image-index')) || 0;
          this.openModal(imageIndex);
        }, true); // Use capture phase
      });
      return;
    }

    // Fallback: Handle masonry gallery and standard images (for non-liquid usage)
    const masonryItems = document.querySelectorAll('.product-media-masonary-item');
    masonryItems.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const dataIndex = item.getAttribute('data-media-position');
        const imageIndex = dataIndex ? parseInt(dataIndex) - 1 : 0;
        this.openModal(imageIndex);
      });
    });

    // Mobile gallery - handle clicks on media items
    const mobileMediaItems = document.querySelectorAll('.product__media-item');
    mobileMediaItems.forEach((mediaItem, index) => {
      // Skip if it's part of masonry gallery
      if (mediaItem.closest('.product-media-masonary-item')) return;

      // Add click handler directly to the media item for better mobile support
      mediaItem.addEventListener('click', (e) => {
        // Only intercept if clicking on image/video, not deferred media buttons
        const isDeferredButton = e.target.closest('.deferred-media__poster-button');
        if (isDeferredButton) return;

        e.preventDefault();
        e.stopPropagation();

        const dataIndex = mediaItem.getAttribute('data-media-position');
        const imageIndex = dataIndex ? parseInt(dataIndex) - 1 : index;
        this.openModal(imageIndex);
      }, true); // Use capture phase for priority
    });

    // Desktop fallback - handle clicks on images and buttons
    const galleryImages = document.querySelectorAll('.product__media-item img, .product__media-item button');
    galleryImages.forEach((element, index) => {
      if (element.closest('.product-media-masonary-item')) return;

      element.addEventListener('click', (e) => {
        e.preventDefault();
        const mediaItem = element.closest('.product__media-item');
        const dataIndex = mediaItem?.getAttribute('data-media-position') || mediaItem?.getAttribute('data-index');
        const imageIndex = dataIndex ? parseInt(dataIndex) - 1 : index;
        this.openModal(imageIndex);
      });
    });

    // Add "View All Images" button if there are multiple images
    const currentImages = this.images[this.activeTab];
    if (currentImages.length > 1) {
      this.addViewAllButton();
    }
  }

  addViewAllButton() {
    // For masonry gallery, add button to the main (first) image
    const masonryMain = document.querySelector('.product-media-masonary--main');
    if (masonryMain && !masonryMain.querySelector('.view-all-images-btn')) {
      const currentImages = this.images[this.activeTab];
      const viewAllBtn = document.createElement('button');
      viewAllBtn.className = 'view-all-images-btn';
      const viewAllText = (this.config.settings?.viewAllImagesText || 'View all {{ count }} images').replace('{{ count }}', currentImages.length);
      viewAllBtn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 7V5C3 3.89543 3.89543 3 5 3H7M21 7V5C21 3.89543 20.1046 3 19 3H17M7 21H5C3.89543 21 3 20.1046 3 19V17M17 21H19C20.1046 21 21 20.1046 21 19V17M9 9H15V15H9V9Z" stroke="currentColor" stroke-width="2"/>
        </svg>
        ${viewAllText}
      `;
      viewAllBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openModal(0);
      });
      masonryMain.style.position = 'relative';
      masonryMain.appendChild(viewAllBtn);
      return;
    }

    // Fallback for standard gallery
    const firstMediaItem = document.querySelector('.product__media-item');
    if (firstMediaItem && !firstMediaItem.querySelector('.view-all-images-btn')) {
      const currentImages = this.images[this.activeTab];
      const viewAllBtn = document.createElement('button');
      viewAllBtn.className = 'view-all-images-btn';
      const viewAllText = (this.config.settings?.viewAllImagesText || 'View all {{ count }} images').replace('{{ count }}', currentImages.length);
      viewAllBtn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 7V5C3 3.89543 3.89543 3 5 3H7M21 7V5C21 3.89543 20.1046 3 19 3H17M7 21H5C3.89543 21 3 20.1046 3 19V17M17 21H19C20.1046 21 21 20.1046 21 19V17M9 9H15V15H9V9Z" stroke="currentColor" stroke-width="2"/>
        </svg>
        ${viewAllText}
      `;
      viewAllBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openModal(0);
      });
      firstMediaItem.style.position = 'relative';
      firstMediaItem.appendChild(viewAllBtn);
    }
  }

  switchTab(tabName) {
    if (tabName === this.activeTab) return;

    this.activeTab = tabName;
    this.currentIndex = 0;

    // Update tab active states
    this.modal.querySelectorAll('.modal-tab').forEach(tab => {
      if (tab.dataset.tab === tabName) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });

    // Update thumbnails
    const thumbGrid = this.modal.querySelector('#modal-thumbnails-grid');
    if (thumbGrid) {
      thumbGrid.innerHTML = this.generateThumbnailsHTML();
    }

    // Update total count
    const currentImages = this.images[this.activeTab];
    const totalSpan = this.modal.querySelector('.total-images');
    if (totalSpan) {
      totalSpan.textContent = currentImages.length;
    }

    // Update current count
    const currentSpan = this.modal.querySelector('.current-image');
    if (currentSpan) {
      currentSpan.textContent = '1';
    }

    // Display first image of new tab
    if (currentImages.length > 0) {
      this.displayImage(0);
    }
  }

  attachSwipeListeners() {
    let startX = 0;
    let endX = 0;
    let isDragging = false;
    const mainContent = this.modal?.querySelector('.modal-main-content');

    if (!mainContent) return;

    // Touch events for mobile/tablet
    mainContent.addEventListener('touchstart', (e) => {
      // Check if touch started on thumbnail area
      const thumbnailArea = e.target.closest('.modal-thumbnails-sidebar');
      if (thumbnailArea) return; // Don't handle swipe for thumbnails

      // Check if touch started on video or iframe
      const isVideo = e.target.tagName === 'VIDEO' || e.target.tagName === 'IFRAME';
      if (isVideo) return; // Don't handle swipe for video/iframe controls

      startX = e.touches[0].clientX;
    }, { passive: true });

    mainContent.addEventListener('touchmove', (e) => {
      // Check if touch is on thumbnail area
      const thumbnailArea = e.target.closest('.modal-thumbnails-sidebar');
      if (thumbnailArea) return; // Allow native scroll for thumbnails

      // Check if touch is on video or iframe
      const isVideo = e.target.tagName === 'VIDEO' || e.target.tagName === 'IFRAME';
      if (isVideo) return; // Allow native video controls

      e.preventDefault();
    }, { passive: false });

    mainContent.addEventListener('touchend', (e) => {
      // Check if touch ended on thumbnail area
      const thumbnailArea = e.target.closest('.modal-thumbnails-sidebar');
      if (thumbnailArea) return; // Don't handle swipe for thumbnails

      // Check if touch ended on video or iframe
      const isVideo = e.changedTouches[0].target.tagName === 'VIDEO' ||
                      e.changedTouches[0].target.tagName === 'IFRAME';
      if (isVideo) return; // Don't handle swipe for video/iframe

      endX = e.changedTouches[0].clientX;
      this.handleSwipe(startX, endX);
    }, { passive: true });

    // Mouse events for desktop
    mainContent.addEventListener('mousedown', (e) => {
      // Check if mouse is on video or iframe
      const isVideo = e.target.tagName === 'VIDEO' || e.target.tagName === 'IFRAME';
      if (isVideo) return; // Don't handle drag for video/iframe controls

      isDragging = true;
      startX = e.clientX;
      mainContent.style.cursor = 'grabbing';
      e.preventDefault(); // Prevent text selection
    });

    mainContent.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
    });

    mainContent.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      endX = e.clientX;
      mainContent.style.cursor = 'grab';
      this.handleSwipe(startX, endX);
    });

    // Reset cursor when mouse leaves
    mainContent.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        mainContent.style.cursor = 'grab';
      }
    });

    // Set initial cursor
    mainContent.style.cursor = 'grab';
  }

  attachFocusTrapListeners() {
    const focusTraps = this.modal?.querySelectorAll('.modal-focus-trap');
    focusTraps?.forEach((trap, index) => {
      trap.addEventListener('focus', () => {
        if (index === 0) {
          // Focus came from before modal, focus last element
          this.focusableElements[this.focusableElements.length - 1]?.focus();
        } else {
          // Focus came from after modal, focus first element
          this.focusableElements[0]?.focus();
        }
      });
    });
  }

  /**
   * Updates the list of focusable elements within the modal for keyboard navigation
   * @returns {void}
   */
  updateFocusableElements() {
    if (!this.modal) return;

    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];

    this.focusableElements = Array.from(
      this.modal.querySelectorAll(focusableSelectors.join(','))
    ).filter(el => !el.classList.contains('modal-focus-trap'));
  }

  /**
   * Opens the modal gallery and displays the specified image
   * @param {number} startIndex - Index of the image to display (default: 0)
   * @returns {void}
   */
  openModal(startIndex = 0) {
    if (this.isOpen) return;

    // Disable Shopify modals first
    this.disableShopifyModals();

    const currentImages = this.images[this.activeTab];
    this.currentIndex = Math.max(0, Math.min(startIndex, currentImages.length - 1));
    this.isOpen = true;
    this.previouslyFocusedElement = document.activeElement;

    // Show modal
    this.modal?.classList.add('active');
    this.lockBodyScroll();

    // Load and display current media
    this.displayImage(this.currentIndex);

    // Focus management
    setTimeout(() => {
      this.modal?.querySelector('.modal-close-btn')?.focus();
    }, 100);

    // Announce to screen readers
    const openMessage = this.config.accessibility?.galleryOpened || 'Gallery opened. Image {{ current }} of {{ total }}';
    const announcement = openMessage.replace('{{ current }}', this.currentIndex + 1).replace('{{ total }}', currentImages.length);
    this.announceToScreenReader(announcement);
  }

  /**
   * Closes the modal gallery with smooth animation and restores page state
   * Unlocks body scroll, restores focus, and announces to screen readers
   * @returns {void}
   */
  closeModal() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Add closing class for animation
    this.modal?.classList.add('closing');

    // Wait for animation to complete before removing
    setTimeout(() => {
      this.modal?.classList.remove('active', 'closing');
      this.unlockBodyScroll();

      // Restore original product images visibility
      this.restoreOriginalImages();

      // Restore focus
      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
        this.previouslyFocusedElement = null;
      }

      // Announce to screen readers
      const closeMessage = this.config.accessibility?.galleryClosed || 'Gallery closed';
      this.announceToScreenReader(closeMessage);
    }, 600); // Match the animation duration
  }

  /**
   * Navigates to the next or previous image in the gallery
   * @param {number} direction - Navigation direction (1 for next, -1 for previous)
   * @returns {void}
   */
  navigateImage(direction) {
    const currentImages = this.images[this.activeTab];
    if (!this.isOpen || currentImages.length <= 1) return;

    const newIndex = this.currentIndex + direction;

    if (newIndex >= 0 && newIndex < currentImages.length) {
      this.changeImage(newIndex);
    }
  }

  changeImage(index) {
    const currentImages = this.images[this.activeTab];
    if (index === this.currentIndex || index < 0 || index >= currentImages.length) return;

    this.currentIndex = index;
    this.displayImage(index);
    this.updateThumbnailActive();
    this.scrollThumbnailIntoView(index);

    // Update counter if enabled
    if (this.config.settings?.showCounter !== false) {
      const currentSpan = this.modal?.querySelector('.current-image');
      if (currentSpan) {
        currentSpan.textContent = (index + 1).toString();
      }
    }

    // Announce to screen readers
    const image = currentImages[index];
    const imageMessage = this.config.accessibility?.imageAnnouncement || 'Image {{ current }} of {{ total }}. {{ alt }}';
    const announcement = imageMessage
      .replace('{{ current }}', index + 1)
      .replace('{{ total }}', currentImages.length)
      .replace('{{ alt }}', image.alt || 'Product image');
    this.announceToScreenReader(announcement);
  }

  /**
   * Displays the image/video at the specified index in the modal
   * Handles images, native videos, and external videos (YouTube, Vimeo)
   * @param {number} index - Index of the media to display
   * @returns {void}
   */
  displayImage(index) {
    const currentImages = this.images[this.activeTab];
    const media = currentImages[index];
    if (!media) return;

    const modalImage = this.modal?.querySelector(`#modal-main-image${this.sectionId ? '-' + this.sectionId : ''}`);
    const modalContainer = this.modal?.querySelector('.modal-main-image-container');
    const loading = this.modal?.querySelector(`#modal-loading${this.sectionId ? '-' + this.sectionId : ''}`);

    if (!modalImage || !loading || !modalContainer) return;

    // Show loading
    loading.style.display = 'block';
    modalImage.classList.remove('loaded');

    // Remove any existing video or iframe elements first
    const existingVideo = modalContainer.querySelector('video.modal-main-image');
    const existingIframe = modalContainer.querySelector('iframe.modal-main-image');
    if (existingVideo) existingVideo.remove();
    if (existingIframe) existingIframe.remove();

    // Handle different media types
    if (media.type === 'video') {
      // Hide img element and create video
      modalImage.style.display = 'none';
      this.createVideoElement(modalContainer, media, loading);
    } else if (media.type === 'external_video') {
      // Hide img element and create iframe
      modalImage.style.display = 'none';
      this.createExternalVideoElement(modalContainer, media, loading);
    } else {
      // Show img element and set source
      modalImage.style.display = 'block';
      this.setImageSrc(modalImage, media, loading);
    }

    // Preload adjacent images
    this.preloadAdjacentImages(index);
  }

  createImageElement(container, media, loading) {
    const img = document.createElement('img');
    img.id = `modal-main-image${this.sectionId ? '-' + this.sectionId : ''}`;
    img.className = 'modal-main-image';
    img.alt = media.alt || '';

    img.onload = () => {
      loading.style.display = 'none';
      img.classList.add('loaded');
    };

    img.onerror = () => {
      loading.style.display = 'none';
      console.warn('Failed to load image:', media.src);
    };

    img.src = media.src;
    container.appendChild(img);
  }

  createVideoElement(container, media, loading) {
    // Remove any existing video elements
    const existingVideo = container.querySelector('video.modal-main-image');
    if (existingVideo) {
      existingVideo.remove();
    }

    const video = document.createElement('video');
    video.id = `modal-main-image${this.sectionId ? '-' + this.sectionId : ''}`;
    video.className = 'modal-main-image';
    video.controls = true;
    video.autoplay = false;
    video.muted = false;
    video.preload = 'metadata';
    video.style.objectFit = 'contain';
    video.style.display = 'block';

    video.onloadeddata = () => {
      loading.style.display = 'none';
      video.classList.add('loaded');
    };

    video.onerror = () => {
      loading.style.display = 'none';
      console.warn('Failed to load video:', media.src);
    };

    // Try to find the best video source
    let videoSrc = null;

    if (media.sources && media.sources.length > 0) {
      const mp4Source = media.sources.find(source => source.format === 'mp4');
      if (mp4Source) {
        videoSrc = mp4Source.url;
      } else {
        videoSrc = media.sources[0].url;
      }
    } else if (media.src && media.src !== 'null' && media.src !== '') {
      videoSrc = media.src;
    }

    if (videoSrc) {
      video.src = videoSrc;
      container.appendChild(video);
    } else {
      console.warn('No valid video source found');
      loading.style.display = 'none';
    }
  }

  createExternalVideoElement(container, media, loading) {
    // Remove any existing iframe elements
    const existingIframe = container.querySelector('iframe.modal-main-image');
    if (existingIframe) {
      existingIframe.remove();
    }

    const iframe = document.createElement('iframe');
    iframe.id = `modal-main-image${this.sectionId ? '-' + this.sectionId : ''}`;
    iframe.className = 'modal-main-image';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '12px';
    iframe.style.display = 'block';
    iframe.allowFullscreen = true;

    let embedUrl = '';
    if (media.host === 'youtube') {
      embedUrl = `https://www.youtube.com/embed/${media.src}?autoplay=0&controls=1&rel=0`;
    } else if (media.host === 'vimeo') {
      embedUrl = `https://player.vimeo.com/video/${media.src}?autoplay=0&controls=1`;
    }

    iframe.onload = () => {
      loading.style.display = 'none';
      iframe.classList.add('loaded');
    };

    iframe.onerror = () => {
      loading.style.display = 'none';
      console.warn('Failed to load external video:', embedUrl);
    };

    if (embedUrl) {
      iframe.src = embedUrl;
      container.appendChild(iframe);
    } else {
      console.warn('No valid external video URL');
      loading.style.display = 'none';
    }
  }

  setImageSrc(modalImage, image, loading) {
    modalImage.onload = () => {
      loading.style.display = 'none';
      modalImage.classList.add('loaded');
    };

    modalImage.onerror = () => {
      loading.style.display = 'none';
      console.warn('Failed to load image:', image.src);
    };

    modalImage.src = image.src;
    modalImage.alt = image.alt || '';
  }

  /**
   * Preloads the first few images for faster display when modal opens
   * @returns {void}
   */
  preloadImages() {
    // Preload first few images of active tab
    const currentImages = this.images[this.activeTab];
    const preloadCount = Math.min(3, currentImages.length);
    for (let i = 0; i < preloadCount; i++) {
      this.preloadImage(i);
    }
  }

  /**
   * Preloads a specific image by index
   * @param {number} index - Index of the image to preload
   * @returns {Promise} Promise that resolves when image is loaded
   */
  preloadImage(index) {
    const key = `${this.activeTab}-${index}`;
    if (this.preloadedImages.has(key)) {
      return Promise.resolve();
    }

    const currentImages = this.images[this.activeTab];
    if (!currentImages[index]) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.preloadedImages.set(key, img);
        resolve();
      };
      img.onerror = reject;
      img.src = currentImages[index].src;
    });
  }

  preloadAdjacentImages(currentIndex) {
    // Preload previous and next images
    const prevIndex = currentIndex - 1;
    const nextIndex = currentIndex + 1;
    const currentImages = this.images[this.activeTab];

    if (prevIndex >= 0) this.preloadImage(prevIndex);
    if (nextIndex < currentImages.length) this.preloadImage(nextIndex);
  }

  updateThumbnailActive() {
    const thumbnails = this.modal?.querySelectorAll('.modal-thumb-item');
    thumbnails?.forEach((thumb, index) => {
      if (index === this.currentIndex) {
        thumb.classList.add('active');
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.classList.remove('active');
        thumb.removeAttribute('aria-current');
      }
    });
  }

  scrollThumbnailIntoView(index) {
    const thumbnail = this.modal?.querySelector(`[data-index="${index}"]`);
    if (thumbnail) {
      thumbnail.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }

  handleThumbnailClick(e) {
    const thumbItem = e.target.closest('.modal-thumb-item');
    if (thumbItem) {
      const index = parseInt(thumbItem.getAttribute('data-index'));
      this.changeImage(index);
    }
  }

  handleThumbnailKeydown(e) {
    const thumbItem = e.target.closest('.modal-thumb-item');
    if (thumbItem && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      const index = parseInt(thumbItem.getAttribute('data-index'));
      this.changeImage(index);
    }
  }

  /**
   * Handles keyboard navigation within the modal
   * Supports: Escape (close), Arrow keys (navigate), Home/End (jump to first/last)
   * @param {KeyboardEvent} e - The keyboard event
   * @returns {void}
   */
  handleKeyboard(e) {
    if (!this.isOpen) return;

    // RTL: the gallery reads right-to-left, so ArrowLeft moves FORWARD and
    // ArrowRight moves back (WAI-ARIA APG carousel pattern). Read from the
    // modal's computed direction so it follows <html dir>. In LTR step is 1,
    // so the two cases below resolve to the original -1 / +1.
    const step = getComputedStyle(this.modal || document.documentElement).direction === 'rtl' ? -1 : 1;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.closeModal();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.navigateImage(-1 * step);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.navigateImage(1 * step);
        break;
      case 'Home':
        e.preventDefault();
        this.changeImage(0);
        break;
      case 'End':
        e.preventDefault();
        const currentImages = this.images[this.activeTab];
        this.changeImage(currentImages.length - 1);
        break;
    }
  }

  /**
   * Handles touch swipe gestures for mobile navigation
   * @param {number} startX - Starting X coordinate of swipe
   * @param {number} endX - Ending X coordinate of swipe
   * @returns {void}
   */
  handleSwipe(startX, endX) {
    // Check if swipe is enabled
    if (this.config.settings?.enableSwipe === false || this.config.navigation?.enableSwipe === false) {
      return;
    }

    const threshold = this.config.navigation?.swipeThreshold || 50;
    const difference = startX - endX;

    if (Math.abs(difference) < threshold) return;

    // RTL: the gallery advances right-to-left, so swiping RIGHT moves forward.
    // In LTR `forward` is exactly the original `difference > 0` test.
    const rtl = getComputedStyle(this.modal || document.documentElement).direction === 'rtl';
    const forward = rtl ? difference < 0 : difference > 0;

    if (forward) {
      // Swiped forward - next image
      this.navigateImage(1);
    } else {
      // Swiped back - previous image
      this.navigateImage(-1);
    }
  }

  handleOutsideClick(e) {
    if (e.target === this.modal) {
      this.closeModal();
    }
  }

  handleResize() {
    // Handle responsive changes if needed
    this.updateFocusableElements();
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  restoreOriginalImages() {
    // Simplified restoration - focus on clearing filter effects
    const productImages = document.querySelectorAll('.product-media-masonary img, .product__media img, .product__media-item img');
    const mediaContainers = document.querySelectorAll('.product-media-masonary-item, .product__media-item, .product__media');
    const viewAllBtn = document.querySelector('.view-all-images-btn');

    // Clear all possible style attributes
    productImages.forEach(img => {
      img.style.opacity = '1';
      img.style.visibility = 'visible';
      img.style.display = 'block';
      img.style.filter = ''; // Clear any filter effects
      img.style.brightness = '';
      img.style.transform = '';
      img.style.background = '';
      img.style.backgroundColor = '';
      img.classList.remove('hidden', 'fade-out', 'invisible');

      // Force image reload if it appears broken
      if (img.naturalWidth === 0 && img.src) {
        const currentSrc = img.src;
        img.src = '';
        setTimeout(() => {
          img.src = currentSrc;
        }, 10);
      }
    });

    // Restore containers - but preserve position for main container
    mediaContainers.forEach(container => {
      container.style.opacity = '1';
      container.style.visibility = 'visible';
      // Don't clear display for containers with the button
      if (!container.querySelector('.view-all-images-btn')) {
        container.style.display = '';
      }
      container.style.filter = '';
      container.style.transform = '';
      container.style.background = '';
      container.style.backgroundColor = '';
      container.classList.remove('hidden', 'fade-out', 'invisible');
    });

    // Ensure button stays in place
    if (viewAllBtn) {
      viewAllBtn.style.position = 'absolute';
      viewAllBtn.style.bottom = '16px';
      viewAllBtn.style.right = '16px';
      viewAllBtn.style.top = 'auto';
      viewAllBtn.style.left = 'auto';
      viewAllBtn.style.transform = 'none';
      viewAllBtn.style.margin = '0';
    }

    // Additional cleanup after a delay
    setTimeout(() => {
      productImages.forEach(img => {
        // Double-check visibility and filter
        if (img.style.opacity === '0' || img.style.visibility === 'hidden' || img.style.display === 'none') {
          img.style.opacity = '1';
          img.style.visibility = 'visible';
          img.style.display = 'block';
        }
        img.style.filter = '';
        img.style.brightness = '';

        // Force repaint
        img.style.transform = 'translateZ(0)';
        setTimeout(() => {
          img.style.transform = '';
        }, 1);
      });

      // Re-ensure button position
      if (viewAllBtn) {
        viewAllBtn.style.position = 'absolute';
        viewAllBtn.style.bottom = '16px';
        viewAllBtn.style.right = '16px';
      }
    }, 200);
  }

  disableShopifyModals() {
    // Disable Shopify's built-in product media modal with protection for product images
    try {
      // Only close modals that are not part of the product gallery
      const shopifyModals = document.querySelectorAll('.product-media-modal, modal-dialog');
      shopifyModals.forEach(modal => {
        if (!modal.closest('.product-media-masonary') && modal.style.display !== 'none') {
          modal.style.display = 'none';
          modal.classList.remove('active', 'open', 'visible');
        }
      });

      // Temporarily disable modal triggers but preserve product gallery functionality
      const shopifyTriggers = document.querySelectorAll('[data-modal-trigger], [data-media-modal]');
      shopifyTriggers.forEach(trigger => {
        if (!trigger.closest('.product-media-masonary')) {
          trigger.style.pointerEvents = 'none';
          setTimeout(() => {
            trigger.style.pointerEvents = '';
          }, 500);
        }
      });

      // Disable Dawn theme modal if present (but not in product gallery)
      if (window.ModalDialog) {
        const modalInstances = document.querySelectorAll('modal-dialog');
        modalInstances.forEach(modal => {
          if (!modal.closest('.product-media-masonary') && modal.close && typeof modal.close === 'function') {
            modal.close();
          }
        });
      }

    } catch (error) {
      console.warn('TripventuraModalGallery: Could not disable all Shopify modals:', error);
    }
  }

  /**
   * Locks body scroll when modal is open and prevents layout shift
   * Calculates scrollbar width and adds padding to prevent content jump
   * @returns {void}
   */
  lockBodyScroll() {
    // Store original values
    this.originalBodyOverflow = document.body.style.overflow;
    this.originalBodyPaddingRight = document.body.style.paddingRight;
    this.originalHtmlOverflow = document.documentElement.style.overflow;

    // Get scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Lock scrolling on both body and html
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Add padding to body to compensate for scrollbar removal
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Prevent touch scrolling on mobile
    document.addEventListener('touchmove', this.preventTouchMove, { passive: false });
  }

  /**
   * Unlocks body scroll when modal closes and restores original state
   * @returns {void}
   */
  unlockBodyScroll() {
    // Restore original overflow values
    document.body.style.overflow = this.originalBodyOverflow;
    document.body.style.paddingRight = this.originalBodyPaddingRight;
    document.documentElement.style.overflow = this.originalHtmlOverflow;

    // Remove touch scroll prevention
    document.removeEventListener('touchmove', this.preventTouchMove, { passive: false });
  }

  preventTouchMove(e) {
    // Only prevent if the touch target is not scrollable content within modal
    const target = e.target;
    const modal = target.closest('.tripventura-modal');
    const scrollableElements = modal?.querySelectorAll('.modal-thumbnails-container, .modal-main-content');

    let isScrollableElement = false;
    scrollableElements?.forEach(element => {
      if (element.contains(target)) {
        isScrollableElement = true;
      }
    });

    if (!isScrollableElement) {
      e.preventDefault();
    }
  }

  // Public API methods
  openGalleryModal(startIndex = 0) {
    this.openModal(startIndex);
  }

  closeGalleryModal() {
    this.closeModal();
  }

  navigateModalImage(direction) {
    this.navigateImage(direction);
  }

  changeModalGalleryImage(index) {
    this.changeImage(index);
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize if we have modal gallery config
  if (window.modalGalleryConfig && window.modalGalleryConfig.product?.images?.length > 0) {
    window.tripventuraGallery = new TripventuraModalGallery();
  }
});

// Add screen reader only styles
if (!document.querySelector('#sr-only-styles')) {
  const srStyles = document.createElement('style');
  srStyles.id = 'sr-only-styles';
  srStyles.textContent = `
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `;
  document.head.appendChild(srStyles);
}
